import { ListChecks, StickyNote } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import { NoticeIcon } from 'common-src/components/base';

import { CLIENT_ROOT } from 'src/constants/paths';
import useTabs from 'src/hooks/useTabs';

import { athenaUrl, isTaskReadinessComplete } from '../../helpers';
import BookingRail from '../BookingRail';
import HuddleNotes from '../HuddleNotes';
import HuddleTasksSection from '../HuddleTasksSection';
import {
  getNoticeDetail,
  getNoticeLabel,
  getNoticeTone,
  MocaDecision,
  MocaOutcomeIcon,
  useMocaPrompt,
} from '../MocaPrompt';
import ProviderAvatars from '../ProviderAvatars';
import {
  CardHeadline,
  clinicVisitTimes,
  visitCode,
  visitLabelWithoutCode,
  VisitSection,
  VisitSections,
} from '../Visit';
import styles from './HuddleVisitCard.module.scss';

const HuddleVisitCard = ({
  appt,
  tasks = [],
  markingTaskId,
  assigningTaskId,
  onMarkComplete,
  onAssignToMe,
  onOpenTask,
  showNotes = true,
  postVisit = false,
  notes,
  onPostNote,
  onEditNote,
  departmentLabel = '',
}) => {
  const { onSelectTab } = useTabs();

  const [open, setOpen] = useState(true);
  const [tasksExpanded, setTasksExpanded] = useState(false);
  const tasksRef = useRef(null);
  const showTasks = () => {
    setOpen(true);
    setTasksExpanded(true);
    requestAnimationFrame(() =>
      tasksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  };
  const moca = useMocaPrompt(appt, 'Huddle');
  const openTasks = tasks.filter((task) => !isTaskReadinessComplete(task));
  const patient = appt?.patient;
  const times = clinicVisitTimes(appt);
  const visit = {
    code: visitCode({ cptCode: appt?.cptCode, visitType: appt?.visitType }),
    kindName: appt?.visitType,
    label: visitLabelWithoutCode(appt?.visitType),
    range: times.range,
  };
  const platformHref =
    patient?.id != null ? `${CLIENT_ROOT}/patients/details/${patient.id}/info` : null;
  const athenaHref = athenaUrl(
    patient?.athenaIntegration?.athenaPatientId,
    patient?.athenaIntegration?.practiceId,
  );

  return (
    <div className={styles.row}>
      <BookingRail
        time={times.start || appt?.time}
        durationMinutes={appt?.durationMinutes}
        appointmentTypeId={appt?.appointmentTypeId}
        zone={times.zone}
      />
      <div className={styles.card}>
        <div>
          <CardHeadline
            layout="inline"
            visit={visit}
            toggle={{ open, onToggle: () => setOpen((prev) => !prev) }}
            aside={
              <>
                {postVisit && <MocaOutcomeIcon appt={appt} onClick={() => setOpen(true)} />}
                {!postVisit && appt?.lowMocaPrompt && (
                  <NoticeIcon
                    tone={getNoticeTone(moca.state)}
                    label={getNoticeLabel(moca.state)}
                    detail={getNoticeDetail(appt.latestMoca, moca.state)}
                    onClick={() => setOpen(true)}
                  />
                )}
                {openTasks.length > 0 && (
                  <NoticeIcon
                    tone="alert"
                    icon={ListChecks}
                    count={openTasks.length}
                    label={`${openTasks.length} outstanding task${openTasks.length === 1 ? '' : 's'}`}
                    detail={openTasks
                      .slice(0, 3)
                      .map((task) => task.title)
                      .filter(Boolean)
                      .join(' \u00b7 ')}
                    onClick={showTasks}
                  />
                )}
                {showNotes && notes?.length > 0 && (
                  <NoticeIcon
                    tone="neutral"
                    icon={StickyNote}
                    count={notes.length}
                    label={`${notes.length} huddle note${notes.length === 1 ? '' : 's'}`}
                    onClick={() => setOpen(true)}
                  />
                )}
                {platformHref && (
                  <button
                    type="button"
                    className={styles.outLink}
                    onClick={() => onSelectTab({ path: platformHref })}
                  >
                    Platform ↗
                  </button>
                )}
                {athenaHref && (
                  <a
                    href={athenaHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.outLink}
                  >
                    Athena ↗
                  </a>
                )}
              </>
            }
          />
          <div
            role="presentation"
            className={styles.sectionsToggle}
            onClick={() => setOpen((prev) => !prev)}
          >
            <VisitSections>
              <VisitSection label="Member">
                <span className={styles.memberName}>{patient?.name}</span>
                {appt?.caregiverNote && <span className={styles.fact}>{appt.caregiverNote}</span>}
              </VisitSection>
              <VisitSection label="Care team">
                {appt?.clinician && <ProviderAvatars name={appt.clinician} jobTitle="Provider" />}
              </VisitSection>
              <VisitSection label="Booking">
                <span className={styles.fact}>
                  {[departmentLabel, appt?.durationMinutes && `${appt.durationMinutes} min booked`]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {appt?.athenaScheduleNote && (
                  <span className={styles.athenaNote}>
                    <StickyNote size={13} className={styles.athenaNoteIcon} aria-hidden="true" />
                    <span>
                      <span className={styles.athenaNoteLabel}>Athena note ·</span>{' '}
                      {appt.athenaScheduleNote}
                    </span>
                  </span>
                )}
              </VisitSection>
            </VisitSections>
          </div>
        </div>

        {open && (
          <>
            {!postVisit && appt?.lowMocaPrompt && (
              <MocaDecision
                latestMoca={appt.latestMoca}
                moca={moca}
                timeZone={appt.departmentTimezone}
                day={appt.departmentLocalDate || appt.date}
              />
            )}

            <HuddleTasksSection
              tasks={tasks}
              expanded={tasksExpanded}
              onExpandedChange={setTasksExpanded}
              sectionRef={tasksRef}
              markingTaskId={markingTaskId}
              assigningTaskId={assigningTaskId}
              onMarkComplete={onMarkComplete}
              onAssignToMe={onAssignToMe}
              onOpenTask={onOpenTask}
            />

            {showNotes && (
              <HuddleNotes notes={notes} onPostNote={onPostNote} onEditNote={onEditNote} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

HuddleVisitCard.propTypes = {
  appt: PropTypes.shape({
    id: PropTypes.string,
    date: PropTypes.string,
    time: PropTypes.string,
    durationMinutes: PropTypes.number,
    visitType: PropTypes.string,
    cptCode: PropTypes.string,
    appointmentTypeId: PropTypes.string,
    clinician: PropTypes.string,
    patient: PropTypes.object,
    caregiverNote: PropTypes.string,
    athenaScheduleNote: PropTypes.string,
    departmentTimezone: PropTypes.string,
    departmentLocalDate: PropTypes.string,
    latestMoca: PropTypes.shape({ score: PropTypes.number, maxScore: PropTypes.number }),
    lowMocaPrompt: PropTypes.bool,
  }).isRequired,
  tasks: PropTypes.array,
  markingTaskId: PropTypes.string,
  assigningTaskId: PropTypes.object,
  onMarkComplete: PropTypes.func.isRequired,
  onAssignToMe: PropTypes.func.isRequired,
  onOpenTask: PropTypes.func.isRequired,
  showNotes: PropTypes.bool,
  postVisit: PropTypes.bool,
  notes: PropTypes.array,
  onPostNote: PropTypes.func,
  onEditNote: PropTypes.func,
  departmentLabel: PropTypes.string,
};

export default HuddleVisitCard;
